// src/pages/account/creator/videos/MyVideos.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { Card, Button, Badge, Row, Col, Form, Spinner, Alert } from "react-bootstrap";

import { db } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";

const PAGE_SIZE = 12;

function normalizeText(s = "") {
  return String(s || "").toLowerCase().trim();
}

export default function MyVideos() {
  const navigate = useNavigate();
  const { user, userDoc, loading } = useAuth();

  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");

  const [qText, setQText] = useState("");
  const [status, setStatus] = useState("all"); // all | active | hidden
  const [kind, setKind] = useState("all");     // all | youtube | podcast | facebook | vertical

  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const canAccess = useMemo(() => {
    const roles = userDoc?.role;
    const arr = Array.isArray(roles) ? roles : [roles].filter(Boolean);
    const set = new Set(arr.map((x) => String(x).toLowerCase()));
    return set.has("admin") || set.has("creator");
  }, [userDoc]);

  const buildBaseQuery = () => {
    // ✅ ของฉันเท่านั้น
    const base = [
      where("ownerUid", "==", user?.uid || ""),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE),
    ];

    // filter status
    if (status === "active") base.unshift(where("isActive", "==", true));
    if (status === "hidden") base.unshift(where("isActive", "==", false));

    // filter kind
    if (kind !== "all") base.unshift(where("kind", "==", kind));

    return query(collection(db, "videos"), ...base);
  };

  const loadFirst = async () => {
    if (!user?.uid) return;
    setErr("");
    setLoadingList(true);
    setHasMore(true);
    setCursor(null);

    try {
      const q = buildBaseQuery();
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setItems(rows);
      setCursor(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoadingList(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดรายการวิดีโอไม่สำเร็จ");
      setLoadingList(false);
    }
  };

  const loadMore = async () => {
    if (!user?.uid || !cursor || !hasMore) return;
    setErr("");
    setLoadingMore(true);

    try {
      // base query แล้วต่อด้วย startAfter(cursor)
      const baseParts = [];
      if (status === "active") baseParts.push(where("isActive", "==", true));
      if (status === "hidden") baseParts.push(where("isActive", "==", false));
      if (kind !== "all") baseParts.push(where("kind", "==", kind));

      baseParts.push(where("ownerUid", "==", user.uid));
      baseParts.push(orderBy("createdAt", "desc"));
      baseParts.push(startAfter(cursor));
      baseParts.push(limit(PAGE_SIZE));

      const q = query(collection(db, "videos"), ...baseParts);
      const snap = await getDocs(q);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      setItems((prev) => [...prev, ...rows]);
      setCursor(snap.docs[snap.docs.length - 1] || cursor);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setLoadingMore(false);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "โหลดเพิ่มไม่สำเร็จ");
      setLoadingMore(false);
    }
  };

  // โหลดครั้งแรก + reload เมื่อ filter เปลี่ยน
  useEffect(() => {
    if (!user?.uid) return;
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, status, kind]);

  const filteredByText = useMemo(() => {
    const t = normalizeText(qText);
    if (!t) return items;
    return items.filter((it) => {
      const title = normalizeText(it.title);
      const url = normalizeText(it.url);
      const cats = Array.isArray(it.categories) ? it.categories.join(" ") : "";
      return (
        title.includes(t) ||
        url.includes(t) ||
        normalizeText(cats).includes(t) ||
        normalizeText(it.kind).includes(t)
      );
    });
  }, [items, qText]);

  if (loading) return null;
  if (!user) return null;
  if (!canAccess) return <Alert variant="warning">หน้านี้สำหรับ Creator/Admin เท่านั้น</Alert>;

  return (
    <div>
      <div className="d-flex align-items-start justify-content-between gap-3 mb-3">
        <div>
          <h4 className="mb-0">วิดีโอของฉัน</h4>
          <div className="text-muted small">
            รายการ <code>videos ที่ฉันสร้าง</code>
          </div>
        </div>

        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={loadFirst} disabled={loadingList || loadingMore}>
            รีเฟรช
          </Button>
          <Button variant="primary" onClick={() => navigate("/account/creator/videos/new")}>
            + เพิ่มวิดีโอ
          </Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {/* Filters */}
      <Card className="border mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={5}>
              <Form.Label>ค้นหา</Form.Label>
              <Form.Control
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder="ค้นหาจากชื่อ, url, หมวดหมู่, kind..."
              />
            </Col>

            <Col md={3}>
              <Form.Label>สถานะ</Form.Label>
              <Form.Select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="active">แสดงผล (Active)</option>
                <option value="hidden">ซ่อน (Hidden)</option>
              </Form.Select>
            </Col>

            <Col md={3}>
              <Form.Label>ประเภท</Form.Label>
              <Form.Select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="all">ทั้งหมด</option>
                <option value="youtube">youtube</option>
                <option value="podcast">podcast</option>
                <option value="facebook">facebook</option>
                <option value="vertical">vertical</option>
              </Form.Select>
            </Col>

            <Col md={1} className="d-grid">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setQText("");
                  setStatus("all");
                  setKind("all");
                }}
              >
                ล้าง
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* List */}
      {loadingList ? (
        <div className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> กำลังโหลดรายการ…
        </div>
      ) : filteredByText.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center py-5">
            <div className="mb-2 fw-semibold">ยังไม่มีวิดีโอ</div>
            <div className="text-muted mb-3">กด “เพิ่มวิดีโอ” เพื่อสร้างรายการแรก</div>
            <Button variant="primary" onClick={() => navigate("/account/creator/videos/new")}>
              + เพิ่มวิดีโอ
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row className="g-3">
            {filteredByText.map((v) => (
              <Col key={v.id} xs={12} md={6} lg={4}>
                <Card className="border h-100">
                  {/* thumb */}
                  <div
                    style={{
                      width: "100%",
                      aspectRatio: "16/9",
                      background: "rgba(127,127,127,.08)",
                      borderTopLeftRadius: 6,
                      borderTopRightRadius: 6,
                      overflow: "hidden",
                    }}
                  >
                    {v.thumbUrl ? (
                      <img
                        src={v.thumbUrl}
                        alt={v.title || "thumb"}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => (e.currentTarget.style.display = "none")}
                      />
                    ) : (
                      <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                        ไม่มีรูปตัวอย่าง
                      </div>
                    )}
                  </div>

                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <Badge bg={v.isActive ? "success" : "secondary"}>
                        {v.isActive ? "Active" : "Hidden"}
                      </Badge>
                      <Badge bg="light" text="dark" style={{ border: "1px solid rgba(0,0,0,.12)" }}>
                        {v.kind || "video"}
                      </Badge>
                    </div>

                    <div className="fw-semibold" style={{ lineHeight: 1.25 }}>
                      {v.title || "(ไม่มีชื่อ)"}
                    </div>

                    {/* <div className="text-muted small mt-2" style={{ wordBreak: "break-all" }}>
                      {v.url ? v.url : v.fileUrl ? v.fileUrl : v.storagePath ? v.storagePath : ""}
                    </div> */}

                    {Array.isArray(v.categories) && v.categories.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mt-3">
                        {v.categories.slice(0, 4).map((c) => (
                          <Badge
                            key={c}
                            bg="light"
                            text="dark"
                            style={{ border: "1px solid rgba(0,0,0,.12)" }}
                          >
                            {c}
                          </Badge>
                        ))}
                        {v.categories.length > 4 && (
                          <Badge bg="light" text="dark" style={{ border: "1px solid rgba(0,0,0,.12)" }}>
                            +{v.categories.length - 4}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="mt-auto pt-3 d-flex gap-2">
                      <Button
                        as={Link}
                        to={`/account/creator/videos/${v.id}`}
                        variant="outline-primary"
                        className="w-100"
                      >
                        แก้ไข
                      </Button>
                     {/*  <Button
                        variant="primary"
                        className="w-100"
                        onClick={() => navigate(`/account/creator/videos/${v.id}`)}
                      >
                        เปิด
                      </Button> */}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Load more */}
          <div className="d-flex justify-content-center mt-4">
            {hasMore ? (
              <Button variant="outline-secondary" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? "กำลังโหลด..." : "โหลดเพิ่ม"}
              </Button>
            ) : (
              <div className="text-muted small">แสดงครบแล้ว</div>
            )}
          </div>

          {/* Hint: ค้นหาทำใน client-side */}
          {/* <div className="text-muted small mt-3">
            * ช่องค้นหาทำแบบ client-side ในรายการที่โหลดมาแล้ว (ถ้าต้องการค้นหาทั้งหมดจริง ๆ ค่อยทำ search index ภายหลัง)
          </div> */}
        </>
      )}
    </div>
  );
}